// Окно сервера iiko в Настройках (открывается кнопкой «⋯» в строке списка): детали, удаление, редактирование
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ResponsiveDialog from './ResponsiveDialog';
import { getAddress, getMonogram } from './ServerList';
import { footerButtonSx, primaryButtonSx } from './ServerForm';
import { CARD_LABEL_COLOR, DIVIDER_COLOR, MOTION, MUTED_TEXT_COLOR, motionSx } from '../styles/tokens';

const ACCENT = '#ff7043';
const DANGER_TEXT = '#ff6b6b';
const DANGER_BG = '#ef5350';
const COPIED_MS = 1500;
const COPY_ICON_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  ...motionSx(['opacity'], MOTION.fast),
};

const textButtonSx = {
  ...footerButtonSx,
  flex: '0 0 auto',
  px: 1.5,
};

const deleteTextSx = {
  ...textButtonSx,
  color: DANGER_TEXT,
  '&:hover': { backgroundColor: 'rgba(255,107,107,0.08)', boxShadow: 'none' },
};

const cancelTextSx = {
  ...textButtonSx,
  color: CARD_LABEL_COLOR,
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', boxShadow: 'none' },
};

const confirmDeleteSx = {
  ...footerButtonSx,
  flex: '0 0 auto',
  backgroundColor: DANGER_BG,
  color: '#fff',
  fontWeight: 700,
  '&:hover': { backgroundColor: '#e53935', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: DANGER_BG, color: '#fff', opacity: 0.7 },
};

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // буфер обмена недоступен (нет HTTPS или разрешения) — молча
    }
  };

  return (
    <IconButton
      onClick={handleCopy}
      aria-label={copied ? 'Адрес скопирован' : 'Скопировать адрес'}
      sx={{ position: 'relative', width: 32, height: 32, color: copied ? '#66bb6a' : MUTED_TEXT_COLOR, ...motionSx(['color'], MOTION.fast) }}
    >
      {/* Иконки друг на друге, смена — crossfade по opacity */}
      <ContentCopyIcon sx={{ ...COPY_ICON_SX, fontSize: 16, opacity: copied ? 0 : 1 }} />
      <CheckIcon sx={{ ...COPY_ICON_SX, fontSize: 18, opacity: copied ? 1 : 0 }} />
    </IconButton>
  );
}

function DetailRow({ label, value, action, first }) {
  return (
    <Box
      sx={{
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
      }}
    >
      <Box component="dt" sx={{ flexShrink: 0, fontSize: '14px', color: MUTED_TEXT_COLOR }}>
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          fontSize: '14px',
          color: '#f5f5f5',
        }}
      >
        <Box component="span" title={value} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '—'}
        </Box>
        {action}
      </Box>
    </Box>
  );
}

// onDelete возвращает Promise: пока он не завершился — спиннер и повторное нажатие заблокировано
function ServerDialog({ server, active = false, open, onClose, onEdit, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Каждое открытие — с деталей, а не с подтверждения
  useEffect(() => {
    if (open) {
      setConfirming(false);
      setDeleting(false);
    }
  }, [open]);

  const name = server?.server_name || server?.name || server?.ip_address || '';
  const address = server ? getAddress(server) : '';

  const handleClose = () => {
    if (!deleting) onClose();
  };

  const handleConfirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={handleClose} labelledBy="server-dialog-title">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Шапка: монограмма, название и адрес, крестик */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: -1, mr: -1 }}>
          <Box
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '15px',
              fontWeight: 800,
              backgroundColor: active ? ACCENT : 'rgba(255,255,255,0.06)',
              color: active ? '#1a0f0a' : '#a1a1aa',
            }}
          >
            {getMonogram(name)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="server-dialog-title"
              component="h2"
              noWrap
              title={name}
              sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1.3 }}
            >
              {name}
            </Typography>
            {address && (
              <Typography noWrap sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                {address}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} aria-label="Закрыть" sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {confirming ? (
          <>
            <Typography role="alert" sx={{ fontSize: '15px', color: '#e4e4e7' }}>
              {`Удалить «${name}»? Дашборд по этому серверу станет недоступен.`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button type="button" onClick={() => setConfirming(false)} disabled={deleting} sx={cancelTextSx}>
                Отмена
              </Button>
              <Button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                aria-busy={deleting}
                sx={confirmDeleteSx}
              >
                {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Удалить'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Детали; пароль не показываем */}
            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow
                first
                label="Адрес"
                value={server?.ip_address}
                action={server?.ip_address ? <CopyButton value={server.ip_address} /> : null}
              />
              <DetailRow label="Порт" value={server?.port !== undefined && server?.port !== null ? String(server.port) : ''} />
              <DetailRow label="Логин" value={server?.login} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Button type="button" onClick={() => setConfirming(true)} sx={deleteTextSx}>
                Удалить
              </Button>
              <Button type="button" onClick={onEdit} sx={{ ...primaryButtonSx, flex: '0 0 auto' }}>
                Редактировать
              </Button>
            </Box>
          </>
        )}
      </Box>
    </ResponsiveDialog>
  );
}

export default ServerDialog;
